/**
 * useStashPosts - reactive per-post units for a derived set of stash ids.
 *
 * One query per post id (the unit), fed by a shared batch loader so any
 * number of missing units costs one /posts/items call per tick. Warm units
 * render instantly and reactively; set changes subscribe/unsubscribe units —
 * a list built on this can never blank while its members are cached.
 */

import { useEffect, useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";

import { upsert } from "@/lib/cachePersistence";
import { createBatchLoader } from "@/lib/batchLoader";
import { mapPostToFeed, type FeedPost } from "@/features/stash/feedPost";
import { stashListQueryKey } from "@/features/stash/stashQueryKeys";
import { postsService, type Post } from "@/services/posts.service";
import { isPostPending } from "@/utils/stashState";
import { claimInFlightReads, IN_FLIGHT_REFRESH_INTERVAL_MS } from "@/features/stash/saveActivity";

const makePostLoader = (skipAuth = false, collectionId?: string, inviteCode?: string) =>
  createBatchLoader<string, FeedPost | null>(
    async ids => {
      const response = await postsService.batchGetPosts(ids, skipAuth, collectionId, inviteCode);
      const rawPosts = Array.isArray(response.posts)
        ? response.posts
        : Object.values((response.posts ?? {}) as Record<string, Post>);

      const byId = new Map<string, FeedPost | null>(ids.map(id => [id, null]));
      for (const raw of rawPosts) {
        const post = mapPostToFeed(raw as Post);
        if (post?.id && byId.has(post.id)) {
          byId.set(post.id, post);
        }
      }
      return byId;
    },
    { retries: 1 },
  );

const postLoader = makePostLoader();

const inFlightReadAt = new Map<string, number>();

export interface StashPostsResult {
  /** Posts for the requested ids, in request order, absent units skipped. */
  posts: FeedPost[];
  /** True only while some requested unit is genuinely absent and fetching. */
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

export function useStashPosts(
  stashIds: string[],
  {
    skipAuth = false,
    collectionId,
    inviteCode,
  }: {
    skipAuth?: boolean;
    collectionId?: string;
    inviteCode?: string;
  } = {},
): StashPostsResult {
  const queryClient = useQueryClient();
  const loader = useMemo(
    () => (skipAuth || collectionId || inviteCode ? makePostLoader(skipAuth, collectionId, inviteCode) : postLoader),
    [skipAuth, collectionId, inviteCode],
  );
  // A collection witness is an access scope, not an account-owned copy of the item.
  const itemKey = (id: string) =>
    collectionId || inviteCode || skipAuth
      ? [...stashListQueryKey(id), { collectionId, inviteCode, skipAuth }]
      : stashListQueryKey(id);
  const queries = useQueries({
    queries: stashIds.map(id => ({
      queryKey: itemKey(id),
      queryFn: async () => {
        const fresh = await loader.load(id);
        const prev = queryClient.getQueryData<FeedPost>(itemKey(id));
        return fresh ? upsert(prev, fresh) : null;
      },
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  const result = useMemo(() => {
    const posts: FeedPost[] = [];
    let missingAndFetching = false;
    for (const query of queries) {
      const post = query.data as FeedPost | null | undefined;
      if (post && !(collectionId && query.isError)) {
        posts.push(post);
      } else if (query.isFetching) {
        missingAndFetching = true;
      }
    }
    return { posts, isLoading: missingAndFetching };
  }, [queries, collectionId]);

  const pendingIds = useMemo(() => result.posts.filter(isPostPending).map(post => post.id), [result.posts]);

  useEffect(() => {
    if (pendingIds.length === 0) return;

    const refresh = async () => {
      const due = claimInFlightReads(pendingIds, inFlightReadAt, Date.now());
      if (due.length === 0) return;
      try {
        const refreshed = await Promise.all(due.map(id => loader.load(id)));
        refreshed.forEach((post, index) => {
          const id = due[index];
          queryClient.setQueryData<FeedPost | null>(itemKey(id), previous => (post ? upsert(previous, post) : null));
        });
      } catch {
        // Keep the last useful item and retry on the next interval.
      }
    };

    const interval = window.setInterval(refresh, IN_FLIGHT_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [loader, pendingIds, queryClient]);

  return {
    ...result,
    isError: queries.some(query => query.isError),
    refetch: () => Promise.all(queries.map(query => query.refetch())),
  };
}
